import React, { useState } from "react";
import { ShieldCheck, Lightning, Wrench, Sparkle, Bank, House, Plug } from "@phosphor-icons/react";

const INTEREST_OPTIONS = [
  { id: "solar", label: "Solar + Battery", icon: Sparkle, desc: "Lock in $7/W pricing" },
  { id: "ecoflow_backup", label: "EcoFlow Backup", icon: Plug, desc: "Portable power station" },
  { id: "equity_funding", label: "Equity Funding", icon: Bank, desc: "Unlock — $0 down upgrades" },
  { id: "hvac", label: "HVAC / Heat pump", icon: Wrench, desc: "Partner installer + rebates" },
  { id: "financing", label: "Solar Financing", icon: Lightning, desc: "Conventional solar loans" },
  { id: "home_improvement", label: "Roof / Home Upgrade", icon: House, desc: "Roofing, windows, insulation" },
  { id: "savings_tips", label: "Just savings tips", icon: Lightning, desc: "Renters / not ready yet" },
];

const TIMES = ["Morning", "Mid-day", "Afternoon", "Evening", "Anytime"];

export default function StepLeadCapture({ defaults, residency = "owner", selectedEcoFlow, onSubmit }) {
  const isRenter = residency === "renter";
  const [form, setForm] = useState({
    full_name: defaults.full_name || "",
    email: "",
    phone: "",
    street: defaults.street || "",
    city: defaults.city || "",
    zip: defaults.zip || "",
    best_time_to_call: "Anytime",
    interest_type: selectedEcoFlow ? "ecoflow_backup" : isRenter ? "savings_tips" : "solar",
    notes: selectedEcoFlow ? `Interested in ${selectedEcoFlow.name} for backup power.` : "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = form.full_name && form.email && form.phone;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="step-lead" className="max-w-3xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 mb-5">
          <ShieldCheck size={14} weight="fill" className="text-emerald-600" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
            Final step
          </span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-950 tracking-tight">
          {isRenter ? "Get connected with a specialist." : "Lock in your savings."}
        </h2>
        <p className="mt-3 text-slate-600 text-lg">
          {isRenter
            ? "We'll match you with the right partner — HVAC, energy efficiency programs, or savings coaching. No pressure."
            : "A Maui Starpower specialist will reach out within 24 hours with a no-pressure quote."}
        </p>
      </div>

      <div className="card-clean p-8 space-y-6">
        {selectedEcoFlow && (
          <div
            data-testid="lead-ecoflow-chip"
            className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200"
          >
            <img src={selectedEcoFlow.image_url} alt={selectedEcoFlow.name} className="w-14 h-14 object-contain flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                Your EcoFlow pick
              </div>
              <div className="font-display font-semibold text-slate-900 truncate">{selectedEcoFlow.name}</div>
              <div className="text-xs text-slate-600">
                {(selectedEcoFlow.capacity_wh / 1000).toFixed(1)} kWh · {selectedEcoFlow.voltage} · from ${selectedEcoFlow.price_usd}
              </div>
            </div>
          </div>
        )}

        <div>
          <span className="block text-sm font-semibold text-slate-700 mb-3">I'm interested in...</span>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {INTEREST_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => update("interest_type", opt.id)}
                data-testid={`interest-${opt.id}`}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                  form.interest_type === opt.id
                    ? "border-sky-500 bg-sky-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <opt.icon
                  size={22}
                  weight="duotone"
                  className={form.interest_type === opt.id ? "text-sky-600" : "text-slate-500"}
                />
                <div className="font-semibold text-slate-900 mt-2">{opt.label}</div>
                <div className="text-xs text-slate-500 mt-1">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Full name *">
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => update("full_name", e.target.value)}
              className="input-clean"
              data-testid="lead-name-input"
              required
            />
          </Field>
          <Field label="Email *">
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className="input-clean"
              data-testid="lead-email-input"
              required
            />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Phone *">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              className="input-clean"
              data-testid="lead-phone-input"
              required
              placeholder="(808) 555-1234"
            />
          </Field>
          <Field label="Best time to call">
            <select
              value={form.best_time_to_call}
              onChange={(e) => update("best_time_to_call", e.target.value)}
              className="input-clean"
              data-testid="lead-time-select"
            >
              {TIMES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-5">
          <Field label="Street">
            <input
              type="text"
              value={form.street}
              onChange={(e) => update("street", e.target.value)}
              className="input-clean"
              data-testid="lead-street-input"
            />
          </Field>
          <Field label="City">
            <input
              type="text"
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
              className="input-clean"
              data-testid="lead-city-input"
            />
          </Field>
          <Field label="Zip">
            <input
              type="text"
              value={form.zip}
              onChange={(e) => update("zip", e.target.value)}
              className="input-clean"
              data-testid="lead-zip-input"
            />
          </Field>
        </div>

        <Field label="Anything we should know?">
          <textarea
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            className="input-clean min-h-[90px] resize-none"
            data-testid="lead-notes-input"
            placeholder="Roof age, HOA, timing, questions..."
          />
        </Field>
      </div>

      <div className="mt-8 flex justify-center">
        <button
          type="submit"
          data-testid="lead-submit-btn"
          disabled={!valid || submitting}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit & Get My Quote"}
        </button>
      </div>
      <p className="text-center text-xs text-slate-500 mt-4">
        We'll never sell your info. By submitting, you agree to be contacted by Maui Starpower.
      </p>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-slate-700 mb-2">{label}</span>
      {children}
    </label>
  );
}
